import React, { useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store';
import { selectCustomLocalizations } from '../../../store/general/general-slice-selectors';
import CustomTable from '../../misc/CustomTable';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CustomLocalizationCode from '../../../models/general/custom-localizations/custom-localization-code';
import CustomLocalization from '../../../models/general/custom-localizations/custom-localization';
import CustomLocalizationForm from './CustomLocalizationForm';
import CustomLocalizationType from '../../../models/enums/custom-localization-type';

const renderCodes = (params: GridRenderCellParams) => {
  const codes: CustomLocalizationCode[] = params.value;
  return codes.flatMap((code) => code.code).join(', ');
};

const CustomLocalizationsTab: React.FC<{
  localizationType: CustomLocalizationType;
  onLocalizationChange: (newType: CustomLocalizationType) => void;
}> = ({ localizationType, onLocalizationChange }) => {
  const { t } = useTranslation();

  const customLocalizations = useAppSelector((state) => selectCustomLocalizations(state));

  const [editingLocalization, setEditingLocalization] = useState<CustomLocalization | null>(null);

  const rowClickHandler = (id: string) => {
    setEditingLocalization(customLocalizations.data?.find((c) => c.id === id) ?? null);
  };

  const closeLocalizationFormHandler = () => {
    setEditingLocalization(null);
  };

  const columns: GridColDef[] = [
    { field: 'title', headerName: t('table-header-title') as string, width: 150 },
    { field: 'description', headerName: t('table-header-description') as string, width: 200 },
    { field: 'titleKey', headerName: t('table-header-localization-key') as string, width: 200 },
    {
      field: 'descriptionKey',
      headerName: t('table-header-localization-description-key') as string,
      width: 200,
    },
    {
      field: 'codes',
      headerName: t('table-header-codes') as string,
      width: 200,
      renderCell: renderCodes,
    },
  ];

  const data: CustomLocalization[] = (customLocalizations.data ?? []).filter(
    (l) => l.type === localizationType,
  );

  return (
    <React.Fragment>
      <Stack spacing={2} height="100%">
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
          {Object.values(CustomLocalizationType).map((type) => (
            <Chip
              key={type}
              label={t('localization-type-' + type)}
              color={type === localizationType ? 'primary' : undefined}
              variant="outlined"
              onClick={() => onLocalizationChange(type)}
            />
          ))}
        </Stack>
        <CustomTable
          getStatus={customLocalizations.rest.getStatus}
          loadingMessage={t('loading-custom-localizations')}
          dataLoaded={customLocalizations.data != null}
          failedToLoadMessage={t('error-loading-custom-localizations')}
          emptyDataMessage={t('info-no-custom-localizations')}
          data={data}
          columns={columns}
          rowClickHandler={rowClickHandler}
        />
      </Stack>
      {editingLocalization != null && (
        <CustomLocalizationForm
          open={editingLocalization != null}
          localizationType={localizationType}
          localization={editingLocalization ?? undefined}
          onClose={closeLocalizationFormHandler}
        />
      )}
    </React.Fragment>
  );
};

export default CustomLocalizationsTab;
